// @date_of_change: 2025-10-09 - Import resolveUploadedKey for S3 URL resolution
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ImageWithFallback from '../ImageWithFallback';
import ImageUploadService from '../../services/ImageUploadService';
import { resolveUploadedKey } from '../../services/ArtworkResolver';
import {
  PREMIUM_PLAN_NAME,
  UPGRADE_ROUTE,
  getTierConfig,
  isPremiumTier
} from '../../config/entitlements';
import { getAssetUsageSnapshot } from '../../services/EntitlementService';
import { resolveRuntimeTier } from '../../utils/runtimeTier';
import './ImageUpload.css';

const ImageUpload = ({
  currentImage,
  onImageUploaded,
  entityName,
  entityType,
  disabled = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [resolvedImageUrl, setResolvedImageUrl] = useState('');
  const [assetUsage, setAssetUsage] = useState({ totalCount: 0, totalBytes: 0 });
  const fileInputRef = useRef(null);
  const tier = resolveRuntimeTier();
  const tierConfig = getTierConfig(tier);
  const isPremium = isPremiumTier(tier);
  const assetCap = tierConfig.limits.assetCount;
  const atAssetCap = !isPremium && assetCap !== 'unlimited' && assetUsage.totalCount >= assetCap;

  const refreshAssetUsage = useCallback(async () => {
    const usage = await getAssetUsageSnapshot();
    setAssetUsage(usage);
  }, []);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const imagePath = await ImageUploadService.uploadImage(file, entityName, entityType);
      onImageUploaded(imagePath);
      await refreshAssetUsage();
      
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadClick = () => {
    if (atAssetCap) {
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // @date_of_change: 2025-10-09 - Resolve S3 URLs when currentImage changes
  useEffect(() => {
    let cancelled = false;

    async function resolveImage() {
      if (!currentImage) {
        setResolvedImageUrl('');
        return;
      }

      // If already a full URL (http/https), use as-is
      if (typeof currentImage === 'string' && /^https?:\/\//i.test(currentImage)) {
        setResolvedImageUrl(currentImage);
        return;
      }

      // If it's an assets path, use as-is
      if (typeof currentImage === 'string' && currentImage.startsWith('/assets/')) {
        setResolvedImageUrl(currentImage);
        return;
      }

      // If it's an uploads path, resolve it
      if (typeof currentImage === 'string' && (currentImage.startsWith('uploads/') || currentImage.startsWith('/uploads/'))) {
        try {
          const resolved = await resolveUploadedKey(currentImage);
          if (!cancelled) {
            setResolvedImageUrl(resolved);
          }
        } catch (err) {
          console.error('[ImageUpload] Failed to resolve S3 URL:', err);
          if (!cancelled) {
            setResolvedImageUrl(currentImage); // Fallback to original
          }
        }
        return;
      }

      // Default: use as-is
      setResolvedImageUrl(currentImage);
    }

    resolveImage();
    return () => { cancelled = true; };
  }, [currentImage]);

  useEffect(() => {
    refreshAssetUsage();
  }, [refreshAssetUsage]);

  return (
    <div className="image-upload-container">
      <div className="current-image-section">
        <h4>Current Image</h4>
        <div className="image-preview">
          <ImageWithFallback
            src={resolvedImageUrl}
            alt={`${entityName} portrait`}
            className=""
          />
        </div>
      </div>

      <div className="upload-section">
        <h4>Upload New Image</h4>
        <p className="upload-instructions">
          Upload a new image for {entityName}. Supported formats: PNG, JPG, SVG (max 5MB)
        </p>
        <div className="upload-tier-note">
          {isPremium
            ? `${PREMIUM_PLAN_NAME}: build your full art vault.`
            : `Free vault: ${assetUsage.totalCount}/${assetCap} uploaded maps and tokens used.`}
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={disabled || isUploading || atAssetCap}
        />
        
        <button
          type="button"
          onClick={handleUploadClick}
          disabled={disabled || isUploading || atAssetCap}
          className={`upload-button ${isUploading ? 'uploading' : ''}`}
        >
          {isUploading ? (
            <>
              <span className="upload-spinner"></span>
              Uploading...
            </>
          ) : atAssetCap ? (
            <>
              <span className="upload-icon">🔒</span>
              Vault Full
            </>
          ) : (
            <>
              <span className="upload-icon">📁</span>
              Choose Image File
            </>
          )}
        </button>

        {uploadError && (
          <div className="upload-error">
            <span className="error-icon">⚠️</span>
            {uploadError}
          </div>
        )}

        {atAssetCap && (
          <div className="upload-upgrade-cta">
            <span>Free vault cap reached. Clear a slot or unlock {PREMIUM_PLAN_NAME}.</span>
            <button type="button" onClick={() => window.location.assign(UPGRADE_ROUTE)}>
              Upgrade
            </button>
          </div>
        )}

        <div className="upload-info">
          <p><strong>File Naming:</strong></p>
          <ul>
            <li>Images will be saved as: <code>{entityName}.{'{ext}'}</code></li>
            <li>If a file exists, you'll be prompted to rename or auto-number</li>
            <li>Saved to: <code>/assets/{entityType}s/</code></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
